import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import ScrollReveal from "./scroll-reveal";
import ImageGalleryModal from "./image-gallery-modal";
import Masonry from "react-masonry-css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

const galleryItems = [
  {
    id: 1,
    title: "Modern Open Office",
    src: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
  {
    id: 2,
    title: "Executive Office Suite",
    src: "https://images.unsplash.com/photo-1541558869434-2840d308329a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
  {
    id: 3,
    title: "Modern Conference Room",
    src: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
  {
    id: 4,
    title: "Creative Workspace",
    src: "https://images.unsplash.com/photo-1497366412874-3415097a27e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
  {
    id: 5,
    title: "Premium Reception Area",
    src: "https://images.unsplash.com/photo-1606744888344-493238951221?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
  {
    id: 6,
    title: "Modern Breakout Area",
    src: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800",
  },
];

export default function GallerySection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMasonry, setIsMasonry] = useState(true);

  const breakpointColumns = {
    default: 3,
    1100: 2,
    700: 1
  };

  const openModal = (index: number) => {
    setCurrentImageIndex(index);
    setModalOpen(true);
  };

  return (
    <section id="gallery" className="py-20 bg-secondary dark:bg-secondary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-playfair font-bold mb-4">
              Design Gallery
            </h2>
            <p className="text-xl text-neutral max-w-2xl mx-auto">
              Explore our portfolio of stunning office transformations
            </p>
            <div className="mt-4">
              <button
                onClick={() => setIsMasonry(!isMasonry)}
                className="px-4 py-2 bg-accent text-white rounded-full hover:bg-accent/90 transition-colors"
              >
                {isMasonry ? "Switch to Grid" : "Switch to Masonry"}
              </button>
            </div>
          </div>
        </ScrollReveal>

        {isMasonry ? (
          <Masonry
            breakpointCols={breakpointColumns}
            className="flex -ml-4 w-auto"
            columnClassName="pl-4 bg-clip-padding"
          >
            {galleryItems.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 0.1}>
                <motion.div
                  className="relative overflow-hidden rounded-2xl hover-lift hover-cursor group mb-4"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => openModal(index)}
                >
                  <LazyLoadImage
                    src={item.src}
                    alt={item.title}
                    effect="blur"
                    className="w-full object-cover"
                    height={Math.random() * 200 + 200} // Random height for masonry effect
                  />
                  <motion.div
                    className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <motion.div
                      className="bg-accent text-white px-6 py-3 rounded-full font-semibold hover-cursor flex items-center space-x-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-expand-alt"></i>
                      <span>View Full Size</span>
                    </motion.div>
                  </motion.div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">{item.title}</h3>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </Masonry>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item, index) => (
              <ScrollReveal key={item.id} delay={index * 0.1}>
                <motion.div
                  className="relative overflow-hidden rounded-2xl hover-lift hover-cursor group"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => openModal(index)}
                >
                  <LazyLoadImage
                    src={item.src}
                    alt={item.title}
                    effect="blur"
                    className="w-full h-64 object-cover"
                  />
                  <motion.div
                    className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <motion.div
                      className="bg-accent text-white px-6 py-3 rounded-full font-semibold hover-cursor flex items-center space-x-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-expand-alt"></i>
                      <span>View Full Size</span>
                    </motion.div>
                  </motion.div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">{item.title}</h3>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>

      <ImageGalleryModal
        images={galleryItems}
        currentIndex={currentImageIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
}
